import {useRef, useState} from "react";
import {Alert, Button, Modal, Spinner} from "react-bootstrap";
import {toast} from "react-toastify";
import {importDepartmentJobMappingCsv} from "../../api-service/department-job-mapping";
import {getApiErrorMessage} from "../../common";

interface CsvImportModalProps {
    show: boolean;
    onClose: () => void;
}

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
const XLSX_EXTENSION_PATTERN = /\.xlsx$/i;
const INVALID_FILE_MESSAGE = "ファイル形式またはファイルサイズが正しくありません。2MB以下のExcelファイル（.xlsx）をアップロードしてください。";

const sampleFormatFileUrl = require("../../assets/documents/test.xlsx");

const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(",")[1] ?? "");
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
};

export function CsvImportModal({show, onClose}: CsvImportModalProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const handleClose = () => {
        setSelectedFile(null);
        setShowConfirm(false);
        onClose();
    };

    const handleFileSelected = (file: File | undefined) => {
        if (!file) return;
        if (!XLSX_EXTENSION_PATTERN.test(file.name) || file.size > MAX_FILE_SIZE_BYTES) {
            toast.error(INVALID_FILE_MESSAGE);
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileSelected(e.dataTransfer.files?.[0]);
    };

    const handleBulkAddClick = () => {
        setShowConfirm(true);
    };

    const handleConfirmBulkAdd = async () => {
        if (!selectedFile) return;
        setIsImporting(true);
        try {
            const fileContent = await readFileAsBase64(selectedFile);
            await importDepartmentJobMappingCsv(fileContent);
            toast.success("アップロードが完了しました。処理結果はログ確認画面でご確認ください。");
            setShowConfirm(false);
            handleClose();
        } catch (error: any) {
            toast.error(getApiErrorMessage(error, "アップロード中にエラーが発生しました。"));
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size={'lg'}>
            <Modal.Header closeButton>Excelで一括追加</Modal.Header>
            <Modal.Body>
                <Alert variant="warning" style={{fontSize:"13px"}}>

                    <svg className="mx-2" width="24" height="21" viewBox="0 0 24 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M23.7024 17.7692L13.1331 0.692308C12.5793 -0.230769 11.3793 -0.230769 10.8255 0.692308L0.256222 17.7692C-0.389932 18.8308 0.256222 20.3077 1.41007 20.3077H22.5485C23.7024 20.3077 24.3947 18.8308 23.7024 17.7692ZM11.9792 16.615C11.1946 16.615 10.5946 16.015 10.5946 15.2303C10.5946 14.4457 11.1946 13.8457 11.9792 13.8457C12.7638 13.8457 13.3638 14.4457 13.3638 15.2303C13.3638 16.015 12.7638 16.615 11.9792 16.615ZM13.3638 12.4616C13.3638 12.7385 13.1792 12.9231 12.9023 12.9231H11.0561C10.7792 12.9231 10.5946 12.7385 10.5946 12.4616V6.46156C10.5946 6.18464 10.7792 6.00002 11.0561 6.00002H12.9023C13.1792 6.00002 13.3638 6.18464 13.3638 6.46156V12.4616Z" fill="#DD7A01"/>
                    </svg>
                    Excelファイルの内容をもとに、キーが一致するデータは更新し、一致しないデータは新規登録します。
                </Alert>

                <h6 className="mt-3 modal-title-header p-2">1. Excelファイルをダウンロード</h6>
                <p className="text-muted fs-13">以下のフォーマットファイルをダウンロードし、編集してください</p>
                <a className="btn-download-excel btn btn-primary btn-sm" role="button"
                   href={sampleFormatFileUrl} download="ジョブと部署コード.xlsx">
                    <svg style={{position: "relative", top: "-2px"}} className="mx-2" width="16" height="16"
                         viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd"
                              d="M14.9229 9.53824H13.9999C13.7537 9.53824 13.5383 9.75363 13.5383 9.99979V13.0768C13.5383 13.3229 13.3229 13.5383 13.0768 13.5383H2.92293C2.67677 13.5383 2.46139 13.3229 2.46139 13.0768V9.99979C2.46139 9.75363 2.246 9.53824 1.99985 9.53824H1.07677C0.830619 9.53824 0.615234 9.75363 0.615234 9.99979V14.1537C0.615234 14.8306 1.16908 15.3845 1.846 15.3845H14.1537C14.8306 15.3845 15.3845 14.8306 15.3845 14.1537V9.99979C15.3845 9.75363 15.1691 9.53824 14.9229 9.53824ZM7.69218 11.5692C7.8768 11.7539 8.15372 11.7539 8.33833 11.5692L12.4922 7.41534C12.6768 7.23072 12.6768 6.95379 12.4922 6.76917L11.846 6.12301C11.6614 5.93839 11.3845 5.93839 11.1999 6.12301L9.4768 7.84611C9.29218 8.03073 8.95372 7.90765 8.95372 7.63073V1.07678C8.92295 0.830622 8.6768 0.615234 8.46141 0.615234H7.53833C7.29218 0.615234 7.0768 0.830622 7.0768 1.07678V7.59996C7.0768 7.87688 6.73833 7.99996 6.55372 7.81534L4.83064 6.09224C4.64603 5.90762 4.3691 5.90762 4.18449 6.09224L3.53833 6.76917C3.35372 6.95379 3.35372 7.23072 3.53833 7.41534L7.69218 11.5692V11.5692Z"
                              fill="#0176D3"/>
                    </svg>
                    フォーマットダウンロード
                </a>

                <h6 className="mt-4 modal-title-header p-2">2. 編集したファイルをアップロード</h6>
                <p className="text-muted fs-13">編集したファイルを選択してください。</p>

                <div
                    className={`csv-dropzone p-4 text-center rounded ${isDragOver ? "csv-dropzone-active" : ""}`}
                    style={{border: "1px dashed #C9C9C9", backgroundColor: selectedFile ? "#EBF7E6" : "#f3f3f3"}}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                >

                    {selectedFile ? (
                        <svg onClick={() => fileInputRef.current?.click()}
                             style={{cursor: "pointer"}}
                             className="mb-2" width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 3.5C11.34 3.5 3.5 11.34 3.5 21C3.5 30.66 11.34 38.5 21 38.5C30.66 38.5 38.5 30.66 38.5 21C38.5 11.34 30.66 3.5 21 3.5ZM21 35C13.2825 35 7 28.7175 7 21C7 13.2825 13.2825 7 21 7C28.7175 7 35 13.2825 35 21C35 28.7175 28.7175 35 21 35ZM29.0325 13.265L17.5 24.7975L12.9675 20.2825L10.5 22.75L17.5 29.75L31.5 15.75L29.0325 13.265Z" fill="#2E844A"/>
                        </svg>
                    ) : (
                        <svg onClick={() => fileInputRef.current?.click()}
                             style={{cursor: "pointer"}}
                             className="mb-2" width="42" height="28" viewBox="0 0 42 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M33.8625 10.57C32.6725 4.5325 27.37 0 21 0C15.9425 0 11.55 2.87 9.3625 7.07C4.095 7.63 0 12.0925 0 17.5C0 23.2925 4.7075 28 10.5 28H33.25C38.08 28 42 24.08 42 19.25C42 14.63 38.4125 10.885 33.8625 10.57ZM33.25 24.5H10.5C6.6325 24.5 3.5 21.3675 3.5 17.5C3.5 13.9125 6.1775 10.92 9.73 10.5525L11.6025 10.36L12.4775 8.6975C14.14 5.495 17.395 3.5 21 3.5C25.585 3.5 29.54 6.755 30.4325 11.2525L30.9575 13.8775L33.635 14.07C36.365 14.245 38.5 16.5375 38.5 19.25C38.5 22.1375 36.1375 24.5 33.25 24.5ZM14 15.75H18.4625V21H23.5375V15.75H28L21 8.75L14 15.75Z" fill="#C9C9C9"/>
                        </svg>
                    )}
                    <br/>

                    <Button style={{color: "#0176D3", border: "1px solid #C9C9C9"}} variant="outline-secondary"
                            size="sm"
                            className="btn-choose-file-xls bg-white"
                            onClick={() => fileInputRef.current?.click()}>
                        <svg className="mx-2" style={{position: "relative", top: "-2px"}} width="16" height="16"
                             viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                  d="M14.9229 9.53769H13.9999C13.7537 9.53769 13.5383 9.78386 13.5383 9.99926V13.0764C13.5383 13.3226 13.3229 13.538 13.0768 13.538H2.92293C2.67677 13.538 2.46139 13.3226 2.46139 13.0764V9.99926C2.46139 9.78386 2.246 9.53769 1.99985 9.53769H1.07677C0.830619 9.53769 0.615234 9.78386 0.615234 9.99926V14.1534C0.615234 14.8303 1.16908 15.3842 1.846 15.3842H14.1537C14.8306 15.3842 15.3845 14.8303 15.3845 14.1534V9.99926C15.3845 9.78386 15.1691 9.53769 14.9229 9.53769ZM8.30759 0.73808C8.12297 0.553453 7.84605 0.553453 7.66143 0.73808L3.50759 4.8922C3.32297 5.07682 3.32297 5.35376 3.50759 5.53839L4.15374 6.18459C4.33836 6.36922 4.61528 6.36922 4.7999 6.18459L6.52297 4.4614C6.70759 4.27677 7.07682 4.39986 7.07682 4.6768V11.2003C7.07682 11.4465 7.26143 11.6619 7.50759 11.6619H8.43067C8.67682 11.6619 8.92297 11.4157 8.92297 11.2003V4.70757C8.92297 4.43063 9.23067 4.30754 9.44605 4.49217L11.1691 6.21536C11.3537 6.39999 11.6307 6.39999 11.8153 6.21536L12.4614 5.56916C12.6461 5.38454 12.6461 5.10759 12.4614 4.92297L8.30759 0.73808Z"
                                  fill="#0176D3"/>
                        </svg>
                        ファイルを選択
                    </Button>
                    {
                        selectedFile ? <p className="mt-2 fs-13">またはファイルをドロップ</p> :
                            <p className="mt-2  fs-13">Excelファイルをドラッグ＆ドロップ</p>
                    }
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        className="d-none"
                        onChange={(e) => handleFileSelected(e.target.files?.[0])}
                    />

                </div>
                {selectedFile && <div className="mb-2 mt-3">
                    <p className="mb-0 fs-13">以下のファイルのアップロードされました</p>
                    <p className="mt-2 mb-0 fw-bold fs-13">ファイル名 : {selectedFile.name}</p>
                </div>
                }

            </Modal.Body>
            <Modal.Footer className="modal-footer">
                <Button variant="light" size="sm" className="modal-btn modal-cancel-btn" onClick={handleClose}>
                    キャンセル
                </Button>
                <Button variant="primary" size="sm" className={`modal-btn ${selectedFile ? '' : 'btn-disabled-common'}`} disabled={!selectedFile}
                        onClick={handleBulkAddClick}>
                    一括追加
                </Button>
            </Modal.Footer>

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered className="confirm-dialog" backdropClassName="modal-backdrop-nested">
                <Modal.Header closeButton>
                    <svg width="17" className="me-2" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M8.49996 0.654297C4.15189 0.654297 0.653809 4.15237 0.653809 8.50045C0.653809 12.8485 4.15189 16.3466 8.49996 16.3466C12.848 16.3466 16.3461 12.8485 16.3461 8.50045C16.3461 4.15237 12.848 0.654297 8.49996 0.654297ZM8.49996 4.61007C9.05573 4.61007 9.48073 5.03507 9.48073 5.59084C9.48073 6.1466 9.05573 6.5716 8.49996 6.5716C7.94419 6.5716 7.51919 6.1466 7.51919 5.59084C7.51919 5.03507 7.94419 4.61007 8.49996 4.61007ZM10.1346 11.4755C10.1346 11.6389 10.0038 11.7697 9.80765 11.7697H7.19227C7.02881 11.7697 6.86535 11.6716 6.86535 11.4755V10.8216C6.86535 10.6581 6.99612 10.462 7.19227 10.462C7.35573 10.462 7.51919 10.3639 7.51919 10.1678V8.86007C7.51919 8.6966 7.38842 8.50045 7.19227 8.50045C7.02881 8.50045 6.86535 8.40237 6.86535 8.20622V7.55237C6.86535 7.38891 6.99612 7.19276 7.19227 7.19276H9.15381C9.31727 7.19276 9.48073 7.35622 9.48073 7.55237V10.1678C9.48073 10.3312 9.6115 10.462 9.80765 10.462C9.97112 10.462 10.1346 10.6255 10.1346 10.8216V11.4755Z" fill="#969492"/>
                    </svg>

                    確認
                </Modal.Header>
                <Modal.Body>
                    <p className="fs-13 mb-0">Excelファイルの内容をもとに、キーが一致するデータは更新し、一致しないデータは新規登録します。</p>
                    <p className="fs-13"> この操作は元に戻せません。よろしいですか？ </p>
                </Modal.Body>
                <Modal.Footer className="modal-footer">
                    <Button variant="light" size="sm" className="modal-btn modal-cancel-btn"
                            onClick={() => setShowConfirm(false)} disabled={isImporting}>
                        キャンセル
                    </Button>
                    <Button size="sm" className="modal-btn btn-save-common" disabled={isImporting}
                            onClick={handleConfirmBulkAdd}>
                        一括追加
                    </Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
}
